import {
  CLEAR_ERROR,
  CLEAR_STATE,
  GET_BONUSES_FAILURE,
  GET_BONUSES_SUCCESS,
  GET_ENTRIES_FAILURE,
  GET_ENTRIES_SUCCESS,
  GET_GAMES_FAILURE,
  GET_GAMES_SUCCESS,
  POST_ENTRY_FAILURE,
  PUT_TEAM_MEMBER_FAILURE,
  PUT_TEAM_MEMBER_SUCCESS,
  SET_HIDE_PRIZE,
  SET_LOADING_DATA_FOR_GAME,
  SET_LOADING_DATA_FOR_HEADER,
  SET_PAGE_FULLY_LOADED,
  SET_PRIZE_FAILURE,
  SET_PRIZE_SUCCESS,
  SET_REVEAL_ANIMATION_PROGRESS,
  SET_SELECTED_GAME_ID_SUCCESS
} from '../types';
import DreamteamService from '../../services/dreamteamService';
import { IGame, IGameEntry, ITeamMember } from '@incentivegames/ig-types/lib/interfaces/apiContracts';
import { getEventsByTeam, clearEvents } from './sportsActions';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';
import { dreamteamUtilities } from 'utilities/dreamteamUtilities';
import { ReactGACategories } from 'constants/appEnums';

const dreamteamService = new DreamteamService();

export const setLoadingDataForHeader = (loadingDataForHeader: boolean) => ({
  type: SET_LOADING_DATA_FOR_HEADER,
  loadingDataForHeader
});

export const getGamesSuccess = (games: IGame[], gameId: number | undefined) => ({
  type: GET_GAMES_SUCCESS,
  payload: {
    games,
    gameId
  }
});

export const getGamesFailure = (error: any) => ({
  type: GET_GAMES_FAILURE,
  error
});

export const getEntriesSuccess = (entries: IGameEntry[], loadingDataForGame: boolean) => ({
  type: GET_ENTRIES_SUCCESS,
  entries,
  loadingDataForGame
});
export const getEntriesFailure = (error: any) => ({
  type: GET_ENTRIES_FAILURE,
  error
});

export const getBonusesSuccess = (bonuses: unknown[] /* TODO: Use the correct type when defined. */) => ({
  type: GET_BONUSES_SUCCESS,
  bonuses
});

export const getBonusesFailure = (error: any) => ({
  type: GET_BONUSES_FAILURE,
  error
});

export const postEntryFailure = (error: any) => ({
  type: POST_ENTRY_FAILURE,
  error
});

export const putTeamMemberSuccess = (teamMember: ITeamMember) => ({
  type: PUT_TEAM_MEMBER_SUCCESS,
  teamMember
});

export const putTeamMemberFailure = (error: any) => ({
  type: PUT_TEAM_MEMBER_FAILURE,
  error
});

export const setRevealAnimationInProgress = (complete: boolean) => ({
  type: SET_REVEAL_ANIMATION_PROGRESS,
  complete
});

export const setPrizeSuccess = (prize: number | undefined) => ({
  type: SET_PRIZE_SUCCESS,
  prize
});

export const setPrizeFailure = (error: any) => ({
  type: SET_PRIZE_FAILURE,
  error
});

export const setLoadingDataForGame = (loadingDataForGame: boolean) => ({
  type: SET_LOADING_DATA_FOR_GAME,
  loadingDataForGame
});

export const setHidePrize = (hidePrize: boolean) => ({
  type: SET_HIDE_PRIZE,
  hidePrize
});

export const clearState = () => ({
  type: CLEAR_STATE
});

export const clearError = () => ({
  type: CLEAR_ERROR
});

export const setSelectedGameIdSuccess = (gameId: number, entry: IGameEntry | undefined) => {
  return {
    type: SET_SELECTED_GAME_ID_SUCCESS,
    selectedGameId: gameId,
    entry
  };
};

export const getGames = () => {
  return (dispatch: any) => {
    dreamteamService
      .getGames()
      .then((response: any) => {
        const games: IGame[] = response?.data?.games;

        let openGames: IGame[] = [];
        const timeNow = new Date().getTime();
        let gameId: number | undefined = undefined;

        // Get open games - note the API orders games by openTime
        if (games?.length) openGames = games.filter((game: IGame) => timeNow >= game.openTime && timeNow <= game.closeTime);

        // Try and select the earliest open game first
        if (openGames?.length) {
          if (openGames.length > 1) dreamteamUtilities.recordEventGA(ReactGACategories.error, 'More than 1 open game error');
          gameId = openGames[0].gameId;
        }

        // If no open games, select the live game
        if (!gameId && games?.length) gameId = games.find((game: IGame) => game.nextGame)?.gameId;

        // If no open games and no live games, select the last closed game
        if (!gameId && games?.length) gameId = games[games.length - 1].gameId;

        dispatch(getGamesSuccess(games, gameId));
      })
      .catch((error: any) => {
        dreamteamUtilities.recordEventGA(ReactGACategories.error, 'Failure', 'Get Games API error');
        dispatch(getGamesFailure(error?.response?.data?.errorCode ?? error));
      });
  };
};

export const getEntries = (token: string, gameId: number) => {
  return (dispatch: any) => {
    dispatch(setLoadingDataForHeader(true));

    dreamteamService
      .getEntries(token, gameId)
      .then((response: any) => {
        const entry = response.data?.entries.find((entry: IGameEntry) => entry.gameId === gameId);
        dispatch(getEntriesSuccess(response.data?.entries, !!entry));
        dispatch(setLoadingDataForHeader(false));
        if (entry) dispatch(getEventsByTeam(entry.team));
      })
      .catch((error: any) => {
        dispatch(setLoadingDataForHeader(false));
        dreamteamUtilities.recordEventGA(ReactGACategories.error, 'Failure', 'Get Entries API error');
        dispatch(getEntriesFailure(error?.response?.data?.errorCode ?? error));
      });
  };
};

export const getBonuses = (token: string) => {
  return (dispatch: any) => {
    dreamteamService
      .getBonuses(token)
      .then((response: any) => {
        dispatch(getBonusesSuccess(response.data?.bonuses));
      })
      .catch((error: any) => {
        dreamteamUtilities.recordEventGA(ReactGACategories.error, 'Failure', 'Get Bonuses API error');
        dispatch(getBonusesFailure(error?.response?.data?.errorCode ?? error));
      });
  };
};

/**
 * It will only update the state if there was a goal
 * @param token
 * @param gameId
 * @param goals Entry totalpoints in the state to compare with the response
 */
export const getEntriesIfNewGoals = (token: string, gameId: number, goals: number) => {
  return (dispatch: any) => {
    dreamteamService
      .getEntries(token, gameId)
      .then((response: any) => {
        const entry: IGameEntry = response.data?.entries.find((entry: IGameEntry) => entry.gameId === gameId);
        if (entry.totalPoints !== goals) {
          dispatch(getEntriesSuccess(response.data?.entries, !!entry));
        }
      })
      .catch((error: any) => {
        dispatch(getEntriesFailure(error?.response?.data?.errorCode ?? error));
      });
  };
};

export const postEntry = (token: string, gameId: number, uniqueId: string, promotion?: string) => {
  return (dispatch: any) => {
    dreamteamService
      .postEntry(token, gameId, uniqueId, promotion)
      .then((response: any) => {
        try {
          communicationUtilities.sendPostMessage.tracking('opt-in');
        } catch (error) {
          dreamteamUtilities.recordEventGA(ReactGACategories.error, 'Failure', `On sending postMessage tracking opt-in -> ' ${error}`);
        }
        dreamteamUtilities.recordEventGA(ReactGACategories.entry, 'Create', 'Success');
        dispatch(getEntries(token, gameId));
      })
      .catch((error: any) => {
        dreamteamUtilities.recordEventGA(ReactGACategories.entry, 'Failure', `On create -> ' ${error?.response?.data?.errorCode ?? error}, time: ${new Date().getTime()}`);
        dispatch(postEntryFailure(error?.response?.data?.errorCode ?? error));
      });
  };
};

export const putTeamMember = (token: string, receipt: string, selectionNumber: number, onSuccessCallback: () => void = () => null) => {
  return (dispatch: any) => {
    dreamteamService
      .putTeamMember(token, receipt, selectionNumber)
      .then((response: any) => {
        dreamteamUtilities.recordEventGA(ReactGACategories.entry, 'Reveal ' + selectionNumber);
        dispatch(putTeamMemberSuccess(response.data));
        onSuccessCallback();
      })
      .catch((error: any) => {
        dreamteamUtilities.recordEventGA(
          ReactGACategories.entry,
          'Failure',
          `On Reveal ${selectionNumber} -> ${error?.response?.data?.errorCode ?? error}, receipt: ${receipt.substring(0, 8)}, time: ${new Date().getTime()}`
        );
        dispatch(putTeamMemberFailure(error?.response?.data?.errorCode ?? error));
      });
  };
};

export const setSelectedGameId = (gameId: number, entries: IGameEntry[] | undefined, token?: string) => {
  return (dispatch: any) => {
    const entry = entries?.find((entry: IGameEntry) => entry.gameId === gameId);
    dispatch(clearEvents());
    dispatch(setSelectedGameIdSuccess(gameId, entry));
    if (token) dispatch(getEntries(token, gameId));
  };
};

export const getPrize = (token: string, receipt: string) => {
  return (dispatch: any) => {
    dreamteamService
      .getPrize(token, receipt)
      .then((response: any) => {
        dreamteamUtilities.recordEventGA(ReactGACategories.prize, 'Revealed - ' + response.data.prizeAmount + '*' + response.data.prizeMultiplier);
        dispatch(setPrizeSuccess(response.data));
      })
      .catch((error: any) => {
        dreamteamUtilities.recordEventGA(
          ReactGACategories.prize,
          'Failure',
          `On Prize reveal -> ${error?.response?.data?.errorCode ?? error}, receipt: ${receipt.substring(0, 8)}, time: ${new Date().getTime()}`
        );
        dispatch(setPrizeFailure(error?.response?.data?.errorCode ?? error));
      });
  };
};

export const setPageFullyLoaded = (pageFullyLoaded: boolean) => ({
  type: SET_PAGE_FULLY_LOADED,
  pageFullyLoaded
});
